import { StackDeploymentInfo } from '@/react/common/stacks/types';
import { useWorkflowGitConfig } from '@/react/portainer/gitops/queries/useWorkflowGitConfig';
import { WorkflowType } from '@/react/portainer/gitops/workflows/types';

import { RepoConfigResponse } from '../types';
import { getGitConfigDivergence } from '../utils';

export interface UseResolvedGitConfigParams {
  workflowId: number;
  stackId: number;
  artifactType: WorkflowType;
  currentDeploymentInfo?: StackDeploymentInfo | null;
}

export function useResolvedGitConfig({
  workflowId,
  stackId,
  artifactType,
  currentDeploymentInfo,
}: UseResolvedGitConfigParams) {
  const {
    gitConfig,
    sourceId,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useWorkflowGitConfig(workflowId, stackId, artifactType);

  const { effective, divergence } = resolveEffectiveGitReference({
    gitConfig,
    sourceId,
    currentDeploymentInfo,
  });

  return {
    hasAuth: !!gitConfig?.Authentication,
    effective,
    divergence,
    isConfigLoading,
    isConfigError,
  };
}

export interface EffectiveGitReference {
  url?: string;
  ref?: string;
  configFilePath?: string;
  commitId?: string;
  sourceId?: number;
}

function resolveEffectiveGitReference({
  gitConfig,
  sourceId,
  currentDeploymentInfo,
}: {
  gitConfig: RepoConfigResponse | undefined;
  sourceId: number | undefined;
  currentDeploymentInfo?: StackDeploymentInfo | null;
}) {
  const divergence = gitConfig
    ? getGitConfigDivergence(gitConfig, currentDeploymentInfo)
    : null;

  const deployed = divergence ? currentDeploymentInfo : undefined;

  const effective: EffectiveGitReference = {
    url: deployed?.RepositoryURL ?? gitConfig?.URL,
    configFilePath: deployed?.ConfigFilePath ?? gitConfig?.ConfigFilePath,
    ref: deployed?.ReferenceName ?? gitConfig?.ReferenceName,
    commitId: deployed?.ConfigHash ?? gitConfig?.ConfigHash,
    sourceId: deployed?.SourceID ?? sourceId,
  };

  return { effective, divergence };
}
